import { useEffect, useState } from "react";
import { Heart } from "lucide-react";
import type { LikeResponse } from "@shared/api-types.js";
import { json, webApi } from "@web-shared/lib/web-api";
import { cn } from "@web-shared/lib/utils";
import { useAuthGate } from "../auth";

/**
 * ♥ and its count. Flips at once and settles on what the server says; a
 * failure puts it back. Signed out, it asks you to sign in and then likes.
 */
export function LikeButton({
  seedId,
  title,
  liked,
  count,
  size = "sm",
}: {
  seedId: string;
  title: string;
  liked: boolean;
  count: number;
  size?: "sm" | "lg";
}) {
  const gate = useAuthGate();
  const [state, setState] = useState({ liked, count });
  const [busy, setBusy] = useState(false);
  // A refetch (sign in, another tab) brings the truth: take it.
  useEffect(() => setState({ liked, count }), [liked, count]);

  const toggle = () =>
    gate.requireSignIn("like", `Sign in to like “${title}”`, () => {
      if (busy) return;
      const next = !state.liked;
      const before = state;
      setState({ liked: next, count: Math.max(0, state.count + (next ? 1 : -1)) });
      setBusy(true);
      webApi<LikeResponse>(`/seeds/${encodeURIComponent(seedId)}/like`, json(next ? "POST" : "DELETE"))
        .then((res) => setState({ liked: res.liked, count: res.likeCount }))
        .catch(() => setState(before))
        .finally(() => setBusy(false));
    });

  return (
    <button
      type="button"
      onClick={toggle}
      aria-pressed={state.liked}
      aria-label={state.liked ? `Unlike ${title}` : `Like ${title}`}
      title={`${state.count} ${state.count === 1 ? "like" : "likes"}`}
      className={cn(
        "inline-flex cursor-pointer items-center gap-1 rounded-md px-2 transition-colors",
        size === "lg" ? "h-9 text-sm" : "h-8 text-xs",
        state.liked ? "text-removed" : "text-muted-foreground hover:text-foreground",
      )}
    >
      <Heart className={cn(size === "lg" ? "size-4" : "size-3.5", state.liked && "fill-current")} />
      <span className="tabular">{state.count}</span>
    </button>
  );
}
